import { type FC, useState } from "react";
import { View, Text, Icon } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { NotificationRowLayout } from "../NotificationRowLayout/NotificationRowLayout";
import {
  NotificationPreferenceGroup,
  VISIBLE_CHANNELS,
  CHANNEL_LABELS,
  type NotificationCategory,
  type NotificationCategoryPreferences,
  type NotificationChannel,
} from "../NotificationPreferenceGroup/NotificationPreferenceGroup";

const CHEVRON_SIZE = 20;

export interface NotificationScopeListItem {
  readonly key: string;
  readonly label: string;
  readonly preferences: NotificationCategoryPreferences;
  /** See `NotificationPreferenceGroupProps.inherited`. */
  readonly inherited?: NotificationCategoryPreferences | undefined;
}

export interface NotificationScopeListGroupProps {
  /** e.g. "Sites" or "Cameras" — the section label shown on the outer accordion. */
  readonly label: string;
  readonly items: readonly NotificationScopeListItem[];
  readonly onToggleChannel: (
    key: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => void;
  /** Starts expanded unless set to `false` — used by stories to pin a collapsed state; the real screen always starts expanded. */
  readonly defaultExpanded?: boolean;
  readonly testID?: string;
}

/**
 * A single collapsible group listing every item of one scope kind — every
 * site, or every camera — each rendered as its own nested, non-collapsible
 * `NotificationPreferenceGroup`. The channel captions (see `VISIBLE_CHANNELS`)
 * show once, in this group's own accordion header row, rather than repeating
 * on every item's row below — with a list of many sites/cameras, that would
 * mean the same captions over and over. Every row (this header included)
 * shares the exact same fixed-width columns (see `NotificationPreferenceGroup`),
 * so one caption row still lines up with every item's checkboxes underneath.
 *
 * Used for both the "Sites" and "Cameras" sections of the notification
 * settings form — same shape either way, just a different label and item list.
 */
export const NotificationScopeListGroup: FC<NotificationScopeListGroupProps> = ({
  label,
  items,
  onToggleChannel,
  defaultExpanded = true,
  testID,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <View style={styles.container} testID={testID}>
      <NotificationRowLayout
        icon={<Icon name={expanded ? "chevronDown" : "chevronRight"} size={CHEVRON_SIZE} />}
        label={label}
        labelTextStyle="sm/semibold"
        onPressLabel={() => setExpanded((prev) => !prev)}
        columns={
          expanded ?
            VISIBLE_CHANNELS.map((channel) => (
              <Text key={channel} textStyle="xxs/regular" color="textMuted">
                {CHANNEL_LABELS[channel]}
              </Text>
            ))
          : []
        }
      />
      {expanded && (
        <View style={styles.rows}>
          {items.map((item) => (
            <NotificationPreferenceGroup
              key={item.key}
              label={item.label}
              preferences={item.preferences}
              inherited={item.inherited}
              collapsible={false}
              onToggleChannel={(category, channel, enabled) => onToggleChannel(item.key, category, channel, enabled)}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.md,
  },
  rows: {
    gap: theme.spacing.md,
    paddingLeft: theme.spacing.md,
  },
}));
